import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { WarningAmber } from '@mui/icons-material';

export type UnsavedChangesDialogProps = {
  open: boolean;
  onClose: () => void;
  onDiscard: () => void;
  onSave: () => Promise<void>;
  saving?: boolean;
  error?: string | null;
};

export const UnsavedChangesDialog = ({
  open,
  onClose,
  onDiscard,
  onSave,
  saving = false,
  error = null,
}: UnsavedChangesDialogProps) => {
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving || isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmber color="warning" />
        Unsaved Changes
      </DialogTitle>
      <DialogContent>
        <DialogContentText>You have unsaved changes. What would you like to do?</DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving || isSaving}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
        <Button onClick={onDiscard} color="error" variant="outlined" disabled={saving || isSaving}>
          Discard Changes
        </Button>
        <Button onClick={onClose} disabled={saving || isSaving}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
